/**
 * Accounting API client for Manifests & Receipts. Contract: docs/MANIFESTS_BACKEND_HANDOFF.md.
 * Every call throws an Error with `.status` on a non-2xx response.
 */

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

/** Endpoint not deployed yet (or backend unreachable) -> the workspace falls back to local demo data. */
export const isEndpointMissing = (err) => !err.status || [404, 405, 501].includes(err.status);

export const createAccountingApi = (BACKEND_URL, fetchWithAuth) => {
  const call = async (path, { method = 'GET', body } = {}) => {
    const isForm = body instanceof FormData;
    const res = await fetchWithAuth(`${BACKEND_URL}/api${path}`, {
      method,
      body: body === undefined || isForm ? body : JSON.stringify(body),
    });
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        detail = (await res.json()).detail || detail;
      } catch (_) {
        /* non-JSON error body */
      }
      throw new ApiError(typeof detail === 'string' ? detail : JSON.stringify(detail), res.status);
    }
    return res.status === 204 ? null : res.json();
  };

  const qs = (params) => new URLSearchParams(params).toString();
  const enc = encodeURIComponent;

  return {
    listDrivers: () => call('/drivers/all'),

    listManifests: (from, to) => call(`/accounting/manifests?${qs({ from, to })}`),
    uploadManifest: (file) => {
      const form = new FormData();
      form.append('file', file);
      return call('/accounting/manifests/upload', { method: 'POST', body: form });
    },
    updateManifest: (id, patch) => call(`/accounting/manifests/${enc(id)}`, { method: 'PATCH', body: patch }),
    reviewPiece: (id, barcode, review) =>
      call(`/accounting/manifests/${enc(id)}/pieces/${enc(barcode)}/review`, { method: 'PUT', body: review }),
    postManifest: (id) => call(`/accounting/manifests/${enc(id)}/post`, { method: 'POST' }),

    getSettings: () => call('/accounting/settings'),

    getRateCards: () => call('/accounting/rate-cards'),
    saveCustomerCard: (card) => call(`/accounting/rate-cards/customers/${enc(card.customer)}`, { method: 'PUT', body: card }),
    saveDriverCard: (card) => call(`/accounting/rate-cards/drivers/${enc(card.driverId)}`, { method: 'PUT', body: card }),
    deleteDriverCard: (driverId) => call(`/accounting/rate-cards/drivers/${enc(driverId)}`, { method: 'DELETE' }),
    saveTaxes: (taxes) => call('/accounting/rate-cards/taxes', { method: 'PUT', body: taxes }),

    listSettlements: (periodStart) => call(`/accounting/settlements?${qs({ period_start: periodStart })}`),
    saveSettlementAdjustments: (id, adjustments) =>
      call(`/accounting/settlements/${enc(id)}`, { method: 'PUT', body: { adjustments } }),
    approveSettlement: (id) => call(`/accounting/settlements/${enc(id)}/approve`, { method: 'POST' }),
    markSettlementProcessed: (id) => call(`/accounting/settlements/${enc(id)}/mark-processed`, { method: 'POST' }),

    listInvoices: (periodStart) => call(`/accounting/manifest-invoices?${qs({ period_start: periodStart })}`),
    createInvoice: (periodStart, customer) =>
      call('/accounting/manifest-invoices', { method: 'POST', body: { period_start: periodStart, customer } }),
    reissueInvoice: (id) => call(`/accounting/manifest-invoices/${enc(id)}/reissue`, { method: 'POST' }),

    listJournal: (from, to) => call(`/accounting/gl/entries?${qs({ from, to })}`),
    listAccounts: () => call('/accounting/gl/accounts'),
    saveAccounts: (accounts) => call('/accounting/gl/accounts', { method: 'PUT', body: accounts }),
  };
};
